'use client'

import * as React from 'react'

import { CaretSortIcon, LayersIcon } from '@radix-ui/react-icons'
import {
  AtLeast,
  Block,
  BlockWithVariants,
  VariantSetBlock,
  getVariantsDataFromKey,
  isBlockWithVariants,
  isInstanceBlock,
  isListBlock,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { Button } from '~/sotano-kit/primitives/button'
import type { TreeManager } from '@basehub/utils/tree/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserStore } from '~/app/_store/user-store'
import {
  useSelectedVariants,
  useVariantSets,
} from '~/hooks/use-selected-variants'
import { getSolidColor } from '../../blocks/variant-set/utils'
import type { VariantData } from '@basehub/data/replicache/index'
import { VariantPills } from '../../blocks/variant-pills'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useTreeSlice } from '~/hooks/use-tree-slice'

type VariantSwitcherProps = {
  blockId: string
  treeManager: TreeManager<Block, NonNullable<unknown>>
}

export const VariantsSwitcher = ({
  blockId,
  treeManager,
}: VariantSwitcherProps) => {
  return (
    <VariantsSwitcherMiddleware
      key={blockId}
      blockId={blockId}
      treeManager={treeManager}
    />
  )
}

// Created so that we can have an outer version that sets a the key with the block id to this middleware, and therefore forcing a remount
const VariantsSwitcherMiddleware = ({
  blockId,
  treeManager,
}: {
  blockId: VariantSwitcherProps['blockId']
  treeManager: React.ComponentProps<typeof VariantsSwitcherInner>['treeManager']
}) => {
  const source = useTreeSlice(
    treeManager,
    (s) => {
      const block = s.getNode(blockId)?.value
      if (!block) return null

      if (isInstanceBlock(block) || isListBlock(block)) {
        const template = s.getNode(block.typeOptions.targetBlockId, {
          required: true,
        }).value

        return template as BlockWithVariants | undefined
      }
      if (!isBlockWithVariants(block)) return null

      return block
    },
    (a, b) =>
      a?.constraints.acceptedVariantSets ===
        b?.constraints.acceptedVariantSets && a?.id === b?.id
  )

  if (!source) return null
  return (
    <VariantsSwitcherInner
      componentBlock={source}
      blockId={blockId}
      treeManager={treeManager}
    />
  )
}

const VariantsSwitcherInner = ({
  componentBlock,
  blockId,
  treeManager,
}: {
  componentBlock: BlockWithVariants
  blockId: string
  treeManager: TreeManager<Block, NonNullable<unknown>>
}) => {
  const { setsInUse } = useVariantSets({ block: componentBlock, treeManager })
  const selectedVariants = useSelectedVariants({
    parentBlock: componentBlock,
    ignoreDefault: false,
  })

  const title = React.useMemo(() => {
    if (!setsInUse || setsInUse.length > 1) return 'Variants'
    return setsInUse[0]?.title || 'Untitled'
  }, [setsInUse])

  const withDefaultValues = setsInUse.map(
    (set) =>
      selectedVariants.find((d) => d.setId === idToSourceBlockId(set)) || {
        variantId: set.value.variants.find((v) => v.isDefault)?.id ?? '',
        setId: idToSourceBlockId(set),
      }
  )

  if (!setsInUse.length) return null

  return (
    <SetsDropdown variantSets={setsInUse} selectedVariants={selectedVariants}>
      <Button
        unstyled
        focusStyled
        className={clsx(
          'text-dark-dim flex items-center justify-center',
          'cursor-default hover:text-dark-bright'
        )}
      >
        <span className="pr-1">
          <LayersIcon className="size-3.5" />
        </span>
        <span className="flex items-center justify-center px-0.5 gap-x-1">
          <span className="capitalize mb-px">{title}:</span>
          <VariantPills
            variants={withDefaultValues}
            uppercase
            blockId={idToSourceBlockId(blockId, treeManager)}
          />
        </span>
        <CaretSortIcon />
      </Button>
    </SetsDropdown>
  )
}

const SetsDropdown = ({
  children,
  variantSets,
  selectedVariants,
}: {
  selectedVariants: VariantData[]
  children: React.ReactNode
  variantSets: AtLeast<VariantSetBlock, 'id' | 'title' | 'value'>[]
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const user = useUserStore((s) => s.optimisticUser)

  const onSelect = React.useCallback(
    async (variant: VariantData) => {
      if (!replicache || !user?.id) return
      await replicache.mutate.toggleSelectedVariant({
        userId: user.id,
        variant,
      })
    },
    [replicache, user?.id]
  )

  React.useEffect(() => {
    if (!user) return

    const href = new URL(window.location.href)
    const variantDataFromUrl = href.searchParams.get('selectedVariants')
    if (!variantDataFromUrl) return

    const data = getVariantsDataFromKey(decodeURIComponent(variantDataFromUrl))
    replicache?.mutate.setSelectedVariants({
      userId: user.id,
      variants: data,
    })
  }, [replicache?.mutate, user, user?.id])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={10}
        className="grid gap-y-0.5 p-1 pt-2 w-[240px]"
      >
        {variantSets.map((set, idx) => (
          <React.Fragment key={set.id}>
            {idx !== 0 && <hr className="border-dark-border-solid my-1" />}
            <DropdownMenuLabel className="!text-dark-faint leading-normal !px-2 !py-0">
              {set.title || 'Untitled'}
            </DropdownMenuLabel>
            {set.value.variants.map((variant) => {
              const isSelected =
                selectedVariants.some(
                  (v) =>
                    v.setId === idToSourceBlockId(set) &&
                    v.variantId === variant.id
                ) ||
                (variant.isDefault &&
                  !selectedVariants.some(
                    (v) => idToSourceBlockId(set) === v.setId
                  ))

              return (
                <DropdownMenuCheckboxItem
                  key={variant.id}
                  className={clsx(
                    'w-full overflow-hidden font-medium text-dark-bright !pl-2 !pr-8 h-7 gap-x-1.5 outline-1 focus:bg-dark-shade',
                    '[&>span:first-child]:right-2 [&>span:first-child]:left-auto',
                    variant.hidden && 'opacity-50'
                  )}
                  onCheckedChange={() =>
                    onSelect({
                      setId: idToSourceBlockId(set),
                      variantId: variant.id,
                    })
                  }
                  checked={isSelected}
                >
                  <div
                    className="size-2 rounded-full shrink-0"
                    style={{
                      backgroundColor: getSolidColor(variant.color),
                    }}
                  />
                  <span className="truncate" title={variant.label}>
                    {variant.label}
                  </span>
                  {variant.isDefault && (
                    <span className="text-xs text-dark-faint font-medium">
                      Default
                    </span>
                  )}
                </DropdownMenuCheckboxItem>
              )
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
